import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { aboutContent } from "@/components/about/about-content";
import { AboutHero } from "@/components/about/about-hero";
import { CredentialsSection } from "@/components/about/credentials-section";
import { PhilosophySection } from "@/components/about/philosophy-section";
import { ProcessTimeline } from "@/components/about/process-timeline";
import { allSiteSettingsQueryOptions } from "@/lib/query-options";

/**
 * About Page Route
 *
 * Showcases Maryam's background, design philosophy, work process,
 * and professional credentials.
 *
 * Features:
 * - SSR for SEO optimization
 * - Semantic HTML structure
 * - Static content structured for CMS migration
 * - Accessible with proper heading hierarchy
 */
export const Route = createFileRoute("/about/")({
	head: () => ({
		meta: [
			{ title: "About Maryam Ahmed | Interior Designer Bahrain" },
			{
				name: "description",
				content:
					"Learn about Maryam Ahmed, a licensed interior designer in Bahrain. CRPEP Category B certified with expertise in residential, commercial, and hospitality design.",
			},
			{
				name: "keywords",
				content:
					"Maryam Ahmed, interior designer, Bahrain, CRPEP licensed, residential design, commercial design, Gulf region",
			},
			{
				property: "og:title",
				content: "About Maryam Ahmed | Interior Designer Bahrain",
			},
			{
				property: "og:description",
				content:
					"Discover the design philosophy and credentials of Maryam Ahmed, a licensed interior designer specializing in luxury residential and commercial spaces.",
			},
			{ property: "og:type", content: "profile" },
			{ property: "og:image", content: "/og-about.jpg" },
		],
	}),
	component: AboutPageComponent,
});

// Type for about settings from CMS
type AboutSettings = {
	hero?: {
		name?: string;
		subtitle?: string;
		photo?: { url?: string } | null;
		bio?: unknown; // Rich text can be complex
	};
	philosophy?: Array<{
		icon?: string;
		title?: string;
		description?: string;
	}>;
	process?: Array<{
		step?: number;
		title?: string;
		description?: string;
	}>;
};

function AboutPageComponent() {
	// Fetch all site settings (includes about)
	const { data: siteSettings } = useQuery({
		...allSiteSettingsQueryOptions(),
		staleTime: 60 * 60 * 1000, // 1 hour
	});

	// Merge CMS data with static fallbacks
	const content = useMemo(() => {
		const cms = (siteSettings?.about as AboutSettings) ?? {};

		return {
			hero: {
				name: cms.hero?.name ?? aboutContent.hero.name,
				subtitle: cms.hero?.subtitle ?? aboutContent.hero.subtitle,
				photo: cms.hero?.photo?.url ?? aboutContent.hero.photo,
				photoAlt: aboutContent.hero.photoAlt, // Keep static alt text
				bio:
					typeof cms.hero?.bio === "string"
						? cms.hero.bio
						: aboutContent.hero.bio,
			},
			philosophy:
				cms.philosophy && cms.philosophy.length > 0
					? cms.philosophy.map((p, i) => ({
							icon: (p.icon ??
								aboutContent.philosophy[i]?.icon ??
								"Sparkles") as "Sparkles" | "Globe" | "Users",
							title: p.title ?? aboutContent.philosophy[i]?.title ?? "",
							description:
								p.description ?? aboutContent.philosophy[i]?.description ?? "",
						}))
					: aboutContent.philosophy,
			process:
				cms.process && cms.process.length > 0
					? cms.process.map((p, i) => ({
							step: p.step ?? i + 1,
							title: p.title ?? aboutContent.process[i]?.title ?? "",
							description:
								p.description ?? aboutContent.process[i]?.description ?? "",
						}))
					: aboutContent.process,
			credentials: aboutContent.credentials, // Keep static credentials for now
		};
	}, [siteSettings]);

	return (
		<div className="min-h-screen">
			{/* Hero Section */}
			<AboutHero
				bio={content.hero.bio}
				name={content.hero.name}
				photoAlt={content.hero.photoAlt}
				photoSrc={content.hero.photo}
				subtitle={content.hero.subtitle}
			/>

			{/* Design Philosophy */}
			<PhilosophySection items={content.philosophy} />

			{/* Design Process Timeline */}
			<ProcessTimeline stages={content.process} />

			{/* Professional Credentials */}
			<CredentialsSection
				education={content.credentials.education}
				license={content.credentials.license}
			/>
		</div>
	);
}
